/**
 * The small marks the funnel is built from.
 *
 * Inline SVG on a 24-unit grid, stroked with `currentColor`, like the navigation rail's set. Drawn
 * here rather than pulled from a package for the same reason: this screen is the one people leave
 * open all day, and an icon font is a second request before it is usable.
 *
 * They are decoration. Every one of them sits beside a word, so each is `aria-hidden` and nothing
 * here is the only way to understand a control.
 */

function Glyph({
  children,
  className = 'board-icon',
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

/** Money still in play: a document, because an open deal is a proposal somewhere. */
export function OpenValueIcon(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M7 3h7l5 5v13H7z" />
      <path d="M14 3v5h5" />
      <path d="M10 13h6M10 17h4" />
    </Glyph>
  );
}

/** The weighted forecast: bars, because it is an estimate made of several stages. */
export function ForecastIcon(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M5 20V10M12 20V4M19 20v-7" />
    </Glyph>
  );
}

/** Won: a trophy. The one figure on this screen that is a fact rather than a projection. */
export function WonIcon(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M8 4h8v5a4 4 0 0 1-8 0z" />
      <path d="M8 6H5v1a3 3 0 0 0 3 3M16 6h3v1a3 3 0 0 1-3 3" />
      <path d="M12 13v4M9 20h6" />
    </Glyph>
  );
}

/** How many deals: stacked layers, because they are spread across stages. */
export function StackIcon(): React.JSX.Element {
  return (
    <Glyph>
      <path d="m12 3 9 5-9 5-9-5z" />
      <path d="m3 13 9 5 9-5M3 17l9 5 9-5" />
    </Glyph>
  );
}

export function SearchGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </Glyph>
  );
}

export function FilterGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M4 5h16l-6 7v6l-4 2v-8z" />
    </Glyph>
  );
}

export function KanbanGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <rect x="4" y="4" width="6" height="16" rx="1" />
      <rect x="14" y="4" width="6" height="10" rx="1" />
    </Glyph>
  );
}

export function ListGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
    </Glyph>
  );
}

/** The clock beside "1 dia na etapa". */
export function ClockGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--xs">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </Glyph>
  );
}

/**
 * The drag handle.
 *
 * Six dots, which is the mark everything draggable has worn for twenty years. It is the affordance
 * that says "this moves" before anybody tries — a card that only reveals it is draggable once you
 * have already dragged it is a card most people never drag.
 */
export function GripGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--grip">
      <circle cx="9" cy="6" r="1.4" />
      <circle cx="15" cy="6" r="1.4" />
      <circle cx="9" cy="12" r="1.4" />
      <circle cx="15" cy="12" r="1.4" />
      <circle cx="9" cy="18" r="1.4" />
      <circle cx="15" cy="18" r="1.4" />
    </Glyph>
  );
}

/** A won deal's tick, drawn on the card itself so the outcome reads without the column header. */
export function WonBadgeGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12 3 3 5-6" />
    </Glyph>
  );
}

/** The empty tray a column shows when it holds nothing. */
export function EmptyTrayGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--lg">
      <path d="M4 13h4l2 3h4l2-3h4" />
      <path d="M5 13 7 5h10l2 8v5H5z" />
    </Glyph>
  );
}

// ---------------------------------------------------------------------------
// The leads screen (Phase 53)
// ---------------------------------------------------------------------------

/** Waiting on us: a clock, because the figure is an age rather than a count. */
export function WaitingGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </Glyph>
  );
}

/** How long until somebody answered: the same clock, which is deliberate — they are one story. */
export function ResponseGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7v5l4 1" />
    </Glyph>
  );
}

/** How many arrived: people, because a lead is a person and not a row. */
export function PeopleGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19c0-3 2.7-5 6-5s6 2 6 5" />
      <path d="M16 6.5a3 3 0 0 1 0 5M17.5 14c2 .6 3.5 2.2 3.5 5" />
    </Glyph>
  );
}

/** Qualification: a funnel, because that is the step it measures. */
export function FunnelGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M4 5h16l-6 7v6l-4 2v-8z" />
    </Glyph>
  );
}

/** Conversion: a bar chart going up, which is the shape of the question. */
export function ConversionGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M5 20v-5M10 20V9M15 20v-8M20 20V5" />
    </Glyph>
  );
}

/** Closing: a target, because it is the one rate that is about hitting something. */
export function TargetGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" />
    </Glyph>
  );
}

/** A source we know the name of. */
export function GlobeGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <circle cx="12" cy="12" r="8" />
      <path d="M4 12h16M12 4a12 12 0 0 1 0 16A12 12 0 0 1 12 4" />
    </Glyph>
  );
}

/** A source nobody recorded. A question mark, because that is exactly what it is. */
export function UnknownGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <circle cx="12" cy="12" r="8" />
      <path d="M10 9.5a2 2 0 1 1 2.6 1.9c-.5.2-.6.6-.6 1.1v.5" />
      <path d="M12 16h.01" />
    </Glyph>
  );
}

export function ImportGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M12 16V4M8 8l4-4 4 4" />
      <path d="M5 16v3h14v-3" />
    </Glyph>
  );
}

export function ExportGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M12 4v12M8 12l4 4 4-4" />
      <path d="M5 16v3h14v-3" />
    </Glyph>
  );
}

export function ColumnsGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <rect x="4" y="5" width="16" height="14" rx="1" />
      <path d="M10 5v14M15 5v14" />
    </Glyph>
  );
}

export function CalendarGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <rect x="4" y="6" width="16" height="14" rx="2" />
      <path d="M8 3v4M16 3v4M4 11h16" />
    </Glyph>
  );
}

/** The arrow on "ver pendências": it goes somewhere, and says so. */
export function ArrowGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M5 12h13M13 7l5 5-5 5" />
    </Glyph>
  );
}

// ---------------------------------------------------------------------------
// The deal screen (Phase 53)
// ---------------------------------------------------------------------------

/** Beside the e-mail on the subtitle line, where the word "e-mail" would be the longer half. */
export function MailGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="m3.5 8 8.5 6 8.5-6" />
    </Glyph>
  );
}

/** The mark on a link that leaves this record for another one. */
export function ExternalGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M14 4h6v6" />
      <path d="M20 4 11 13" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </Glyph>
  );
}

/** The deal was won: a tick, on the button that says so. */
export function CheckGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="m5 12 5 5 9-10" />
    </Glyph>
  );
}

/** The deal was lost. A cross rather than a red tick, because the two must not look alike. */
export function CrossGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="m6 6 12 12M18 6 6 18" />
    </Glyph>
  );
}

/** A deal with nothing priced yet: an empty box, which is what it is. */
export function BoxGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--lg">
      <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9z" />
      <path d="m4 7.5 8 4.5 8-4.5M12 12v9" />
    </Glyph>
  );
}

/** What somebody wrote down about the deal. */
export function NoteGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M15 3v4h4" />
      <path d="M9 12h7M9 16h5" />
    </Glyph>
  );
}

/** The next thing somebody has promised to do. */
export function FlagGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M6 21V4h12l-3 4 3 4H6" />
    </Glyph>
  );
}
